import { useTranslation } from "react-i18next";
import React, { useState, useEffect, useMemo } from "react";
import { Form, Input, Spin } from "antd";
import TextArea from "antd/es/input/TextArea";

import { convertToDayjs, formatToLocaleDate } from "../../../../utils/dateUtils";
import { ClassTask, ClassWithSubjects } from "../../../../types/classTaskTypes";
import LocalizedDatePicker from "components/Common/ui/LocalizedDatePicker";
import CommonSelectLite from "components/Common/CommonSelectLite";
import { getSubjectLabelFromCode } from "utils/schoolUtils";
import CustomFileUpload from "components/Common/ui/Files/CustomFileUpload";

interface TaskFormProps {
  form: any;
  initialValues?: Partial<ClassTask> & Record<string, any>;
  classData: ClassWithSubjects[];
  subjects: { value: string; label: string }[];
  isEditing: boolean;
}

function normalizeInitial(raw: any) {
  const typeNorm =
    raw?.type === "lessonPlan" ? "LessonPlan" :
      raw?.type === "homework" ? "Homework" : raw?.type;

  const subjectId =
    raw?.subjectId ??
    raw?.["classSubject.subject.id"] ??
    undefined;

  const subjectCode =
    raw?.subjectCode ??
    raw?.["classSubject.subject.code"] ??
    undefined;

  const classLabel = raw?.className ?? undefined;

  let attachments = [];
  if (raw?.attachments) {
    if (Array.isArray(raw.attachments)) {
      attachments = raw.attachments;
    } else {
      attachments = JSON.parse(raw.attachments);
    }
  }

  const { attachment, ...rest } = raw || {};

  return {
    ...rest,
    type: typeNorm,
    subjectId,
    subjectCode,
    classLabel,
    attachments, // Array of URLs
  };
}

const TaskForm: React.FC<TaskFormProps> = ({
  form,
  initialValues,
  classData,
  subjects,
  isEditing,
}) => {
  const { t } = useTranslation();
  const norm = useMemo(() => normalizeInitial(initialValues ?? {}), [initialValues]);

  const maxSizeMB = process.env.REACT_APP_MAX_FILE_SIZE_MB ?
    parseInt(process.env.REACT_APP_MAX_FILE_SIZE_MB) : 2;

  const [selectedSubjectId, setSelectedSubjectId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [removedExistingFiles, setRemovedExistingFiles] = useState<string[]>([]);

  const handleFilesChange = (files: File[]) => {
    setUploadedFiles(files);
    form.setFieldsValue({ attachments: files });
  };

  const handleRemoveExisting = (url: string) => {
    setRemovedExistingFiles(prev => [...prev, url]);
    const currentRemoved = form.getFieldValue('removedFiles') || [];
    form.setFieldsValue({
      removedFiles: [...currentRemoved, url]
    });
  };

  useEffect(() => {
    let mounted = true;

    (async () => {
      if (mounted) {
        form.resetFields();

        // Reset file states
        setUploadedFiles([]);
        setRemovedExistingFiles([]);

        const initial = {
          ...norm,
          date: norm?.date ? convertToDayjs(norm.date) : null,
          dueDate: norm?.dueDate ? convertToDayjs(norm.dueDate) : null,
          attachments: [],
          removedFiles: [],
        };

        form.setFieldsValue(initial);

        if (isEditing) {
          if (norm.subjectId) setSelectedSubjectId(String(norm.subjectId));
        } else {
          if (!selectedSubjectId && subjects.length === 1) {
            const onlySubject = subjects[0].value;
            setSelectedSubjectId(onlySubject);
            form.setFieldsValue({ subjectId: onlySubject, classId: undefined });
          } else {
            setSelectedSubjectId(null);
            form.setFieldsValue({ subjectId: undefined, classId: undefined });
          }
        }

        setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [isEditing, norm?.id, initialValues, subjects, form]);

  const filteredClasses = useMemo(() => {
    if (!selectedSubjectId) return [];
    const filtered = classData
      .filter((cls) =>
        cls.subjects.some((subj) => subj.subjectId === selectedSubjectId)
      )
      .map((cls) => ({ value: cls.classId, label: cls.className }));

    return Array.from(new Map(filtered.map((c) => [c.value, c])).values());
  }, [selectedSubjectId, classData]);

  const getSubjectName = (id?: string) =>
    subjects.find((s) => s.value === id)?.label || "-";

  const getClassName = (id?: string) =>
    classData.find((c) => c.classId === id)?.className || "-";

  const getTypeName = (type?: string) => {
    if (type === "LessonPlan") return t("lesson_plan");
    if (type === "Homework") return t("homework");
    return "-";
  };

  if (loading) {
    return (
      <div style={{ padding: 12 }}>
        <Spin tip={t("loading")} />
      </div>
    );
  }

  return (
    <Form
      key={isEditing ? (norm?.id ?? "edit") : "create"}
      form={form}
      layout="vertical"
      preserve={false}
      initialValues={{
        ...norm,
        date: norm?.date ? convertToDayjs(norm.date) : null,
        dueDate: norm?.dueDate ? convertToDayjs(norm.dueDate) : null,
        attachments: [],
        removedFiles: [],
      }}
    >
      {/* TYPE */}
      {isEditing ? (
        <Form.Item label={t("task_type")}>
          <Input value={getTypeName(norm?.type)} readOnly disabled />
        </Form.Item>
      ) : (
        <Form.Item
          name="type"
          label={t("task_type")}
          rules={[{ required: true, message: t("select_a_task_type") }]}
          valuePropName="value"
          getValueFromEvent={(v) => v}
        >
          <CommonSelectLite
            options={[
              { value: "LessonPlan", label: t("lesson_plan") },
              { value: "Homework", label: t("homework") },
            ]}
            optionValueKey="value"
            optionLabelKey="label"
            placeholder={t("select_a_task_type")}
          />
        </Form.Item>
      )}

      {/* SUBJECT */}
      {isEditing ? (
        <Form.Item label={t("subject")}>
          <Input
            value={
              getSubjectName(norm?.subjectId ?? "") ||
              getSubjectLabelFromCode(norm?.subjectCode ?? "")
            }
            readOnly
            disabled
          />
        </Form.Item>
      ) : (
        <Form.Item
          name="subjectId"
          label={t("subject")}
          rules={[{ required: true, message: t("select_subject_for_exam") }]}
          valuePropName="value"
          getValueFromEvent={(v) => v}
        >
          <CommonSelectLite
            options={subjects}
            optionValueKey="value"
            optionLabelKey="label"
            placeholder={t("select_subject_for_exam")}
            onChange={(value: string) => {
              setSelectedSubjectId(value);
              form.setFieldsValue({ classId: undefined });
            }}
          />
        </Form.Item>
      )}

      {/* CLASS */}
      {isEditing ? (
        <Form.Item label={t("class")}>
          <Input
            value={norm?.classLabel || getClassName(norm?.classId)}
            readOnly
            disabled
          />
        </Form.Item>
      ) : (
        <Form.Item
          name="classId"
          label={t("class")}
          rules={[{ required: true, message: t("select_a_class") }]}
          valuePropName="value"
          getValueFromEvent={(v) => v}
        >
          <CommonSelectLite
            options={filteredClasses}
            optionValueKey="value"
            optionLabelKey="label"
            placeholder={t("select_a_class")}
            disabled={!selectedSubjectId}
          />
        </Form.Item>
      )}

      {/* DATE */}
      {isEditing ? (
        <Form.Item label={t("task_date")}>
          <Input
            value={norm?.date ? formatToLocaleDate(norm.date) : "-"}
            readOnly
            disabled
          />
        </Form.Item>
      ) : (
        <Form.Item
          name="date"
          label={t("task_date")}
          rules={[{ required: true, message: t("select_a_date") }]}
        >
          <LocalizedDatePicker
            style={{ width: "100%" }}
            placeholder={t("select_a_date")}
            fromToday
          />
        </Form.Item>
      )}

      {/* TOPIC */}
      <Form.Item
        name="topic"
        label={t("task_topic")}
        rules={[{ required: true, message: t("enter_a_title") }]}
      >
        <Input placeholder={t("task_topic")} />
      </Form.Item>

      {/* DESCRIPTION */}
      <Form.Item
        name="description"
        label={t("task_description")}
        rules={[{ required: true, message: t("enter_a_description") }]}
      >
        <TextArea
          placeholder={t("task_description")}
          maxLength={250}
          showCount
          autoSize={{ minRows: 3, maxRows: 6 }}
        />
      </Form.Item>

      {/* DUE DATE */}
      <Form.Item
        name="dueDate"
        label={t("due_date")}
        dependencies={["date"]}
        rules={[
          { required: true, message: t("select_a_date") },
          ({ getFieldValue }) => ({
            validator(_, value) {
              const taskDate = getFieldValue("date");
              if (!value || !taskDate) return Promise.resolve();
              if (value.isAfter(taskDate, "day")) return Promise.resolve();
              return Promise.reject(
                new Error(t("due_date_must_be_after_task_date"))
              );
            },
          }),
        ]}
      >
        <LocalizedDatePicker
          style={{ width: "100%" }}
          placeholder={t("select_a_date")}
          fromToday
        />
      </Form.Item>

      {/* FILE UPLOAD */}
      <Form.Item
        label={t("attachments")}
        name="attachments"
        valuePropName="files"
        getValueFromEvent={(e) => {
          if (Array.isArray(e)) return e;
          return e?.fileList;
        }}
      >
        <CustomFileUpload
          value={uploadedFiles}
          onChange={handleFilesChange}
          multiple={true}
          maxCount={5}
          maxSizeMB={maxSizeMB}
          accept=".pdf,.jpg,.jpeg,.png"
          disabled={false}
          existingFiles={norm.attachments?.filter((url: string) =>
            !removedExistingFiles.includes(url)
          ) || []}
          onRemoveExisting={handleRemoveExisting}
          showUploadList={true}
          listType="picture-card"
          dragAndDrop={true}
        />
      </Form.Item>

      <Form.Item name="removedFiles" hidden>
        <Input type="hidden" />
      </Form.Item>
    </Form>
  );
};

export default TaskForm;